class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegated: cart.js replaces .drawer__inner on every quantity change, so a
    // listener bound to the bump button itself would be lost on every repaint.
    this.addEventListener('click', (evt) => {
      const button = evt.target?.closest?.('[data-bump-add]');
      if (button) this.addBump(button);
    });
  }

  connectedCallback() {
    // Page load and drawer removals (cart.js repaints, then publishes
    // `cart-items`) never pass through renderContents, so they run joinBumpLines
    // here. Only a `cart-items` event that repainted the drawer carries a fresh
    // DOM: product-form publishes before it paints, the /cart page's cart-items
    // events carry no drawer section, and the other sources do not repaint the
    // drawer. A stale DOM must never drive an absolute write.
    if (!this.bumpUnsubscriber && typeof subscribe === 'function') {
      this.bumpUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'cart-items') return;
        if (!event.cartData || event.cartData.errors || event.cartData.status) return;
        if (!event.cartData.sections?.['cart-drawer']) return;
        this.joinBumpLines();
      });
    }

    // The page-load heal: an orphaned twin left by a previous page view is
    // swapped back to full price as soon as the drawer connects.
    this.joinBumpLines();
  }

  disconnectedCallback() {
    if (this.bumpUnsubscriber) {
      this.bumpUnsubscriber();
      this.bumpUnsubscriber = undefined;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  paintSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    document.getElementById('CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.paintSections(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.joinBumpLines();
    });
  }

  addBump(button) {
    if (this.bumpBusy || button.getAttribute('aria-disabled') === 'true') return;
    this.bumpBusy = true;
    button.setAttribute('aria-disabled', 'true');

    const id = Number(button.getAttribute('data-bump-add'));
    const body = JSON.stringify({
      id,
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_add_url, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = response.description || response.message || window.cartStrings.error;
          button.removeAttribute('aria-disabled');
          return;
        }

        this.paintSections(response);

        const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
        if (liveRegion) liveRegion.textContent = button.getAttribute('data-bump-status');

        const drawer = document.getElementById('CartDrawer');
        const item = drawer?.querySelector(`.cart-item [data-quantity-variant-id="${id}"]`)?.closest('.cart-item');
        const target = item?.querySelector('.cart-item__name') || drawer?.querySelector('.cart-item__name');
        trapFocus(drawer, target);

        // `source: 'cart-items'` is deliberate: the drawer has already repainted
        // itself from this response, so CartItems.onCartUpdate must not refetch and
        // drop the focus we just moved. The drawer's own subscriber only acts when
        // the section was repainted, and the /cart hand-off reload still runs.
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            productVariantId: String(id),
            cartData: response,
          });
        }
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        button.removeAttribute('aria-disabled');
      })
      .finally(() => {
        this.bumpBusy = false;
      });
  }

  readBumpStamp(drawer, name) {
    const marker = drawer?.querySelector(`[${name}]`);
    if (!marker) return null;

    const from = marker.getAttribute(name);
    const into = marker.getAttribute(`${name}-into`);
    const quantity = parseInt(marker.getAttribute(`${name}-quantity`), 10);
    if (!from || !into || !(quantity > 0)) return null;
    return { marker, from, into, quantity };
  }

  joinBumpLines() {
    const drawer = document.getElementById('CartDrawer');
    // Swap first: revoke the 10% before considering any join.
    const stamp = this.readBumpStamp(drawer, 'data-bump-swap') || this.readBumpStamp(drawer, 'data-bump-join');
    if (!stamp) return;

    const { marker, from, into, quantity } = stamp;

    // A completed swap consumes the stamp *element* it acted on: the repaint drops
    // that node, so its recursion and any duplicate delivery of the same event see
    // it again only from a stale snapshot and must not resend. A recreated twin is
    // a fresh element carrying the same identity, and that one must swap again.
    this.bumpJoinDone ||= new WeakSet();
    if (this.bumpJoinDone.has(marker)) return;

    // Every stamp identity is attempted at most once per page, not just the latest:
    // A, then B, then A must not retry A. A failed or in-flight identity stays in
    // the set, so a failed write is never re-sent within the page view. Only a
    // completion frees the identity, and only the element it completed on.
    const attempt = `${from}>${into}>${quantity}`;
    this.bumpJoinAttempts ||= new Set();
    if (this.bumpJoinAttempts.has(attempt)) return;
    this.bumpJoinAttempts.add(attempt);

    const sections = this.getSectionsToRender().map((section) => section.id);
    fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates: { [from]: 0, [into]: quantity },
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status || !state.sections) return;
        // A write that did not remove the `from` line paints and publishes nothing,
        // so the /cart hand-off reload can never loop.
        if (Array.isArray(state.items) && state.items.some((line) => line.key === from)) return;
        // Completed: free the identity for a recreated twin and remember the exact
        // element so the repaint's recursion cannot swap it twice.
        this.bumpJoinAttempts.delete(attempt);
        this.bumpJoinDone.add(marker);
        this.paintSections(state);
        const freshDrawer = document.getElementById('CartDrawer');
        if (this.classList.contains('active')) {
          trapFocus(freshDrawer, freshDrawer?.querySelector('.cart-item__name'));
        }
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        }
        this.joinBumpLines();
      })
      .catch(() => {});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
